import { IconArrowsMaximize, IconFileDownload, IconHighlight, IconPrinter, IconRotate, IconX } from "@tabler/icons-react";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { Document, Page, pdfjs } from "react-pdf";
import "react-pdf/dist/esm/Page/AnnotationLayer.css";
import "react-pdf/dist/esm/Page/TextLayer.css";
import { Button } from "@/components/core/buttons/button.tsx";
import { useEvidenceSubmarkingMutation } from "@/features/case-evaluation/api/queries/evidence-query";
import CaseEvidenceEditModal from "@/features/case-evaluation/components/case-evidence-edit-modal";
import type { CaseEvidence, CaseEvidenceForm } from "@/features/case-evaluation/types/evidence";
import { downloadFile } from "@/utils/download-file";
import moment from "moment";
import ConfirmWithPasswordModal from "../confirm-with-password-modal";
import { Input } from "../core/forms/input";
import { useModal } from "../core/modals/use-modal";
import { ScrollArea } from "../core/scroller";
import Tooltip from "../core/tooltip";

const maxWidth = 800;
// pdfjs.GlobalWorkerOptions.workerSrc = new URL("pdfjs-dist/build/pdf.worker.min.mjs", import.meta.url).toString();
pdfjs.GlobalWorkerOptions.workerSrc = "https://cdnjs.cloudflare.com/ajax/libs/pdf.js/4.8.69/pdf.worker.min.mjs";

export function PdfViewer({
	file,
	documents,
	evidence,
}: {
	file: any;
	documents: any;
	evidence?: CaseEvidence;
}) {
	const modal = useModal();
	const [inputValue, setInputValue] = useState("1");
	const [currentPage, setCurrentPage] = useState(1);
	const [totalPages, setTotalPages] = useState(0);
	const [rotateValue, setRotateValue] = useState(0);

	const [fullscreen, setFullscreen] = useState(false);
	const [selectedText, setSelectedText] = useState("");
	const [highlighting, setIsHighlighting] = useState(false);
	const [showButton, setShowButton] = useState(false);
	const [buttonPosition, setButtonPosition] = useState({ top: 0, left: 0 });
	const caseEvidenceSubmarkingMutation = useEvidenceSubmarkingMutation();
	const contentRef = useRef<HTMLDivElement>(null);
	const pageRefs = useRef([]);

	const handleInputChange = (event) => {
		const value = event.target.value;
		setInputValue(value);

		const parsed = Number.parseInt(value, 10);

		if (!Number.isNaN(parsed) && parsed >= 1 && parsed <= totalPages) {
			setCurrentPage(parsed);

			setTimeout(() => {
				const targetRef = pageRefs.current[parsed - 1];
				if (targetRef?.current) {
					targetRef.current.scrollIntoView({ behavior: "smooth", block: "start" });
				}
			}, 0);
		}
	};

	const handleDownload = (document: Media) => {
		downloadFile(document.original_url, document.file_name);
	};

	const handlePrint = () => {
		alert("Feature coming soon");
		// const blob = new Blob([file], { type: "application/pdf" });
		// PrintJs(URL.createObjectURL(blob));
	};

	const handleMouseUp = useCallback(() => {
		setIsHighlighting(true);
		const selection = document.getSelection();
		const text = selection?.toString();
		const selectedNode = selection?.anchorNode?.parentElement;

		if (contentRef.current?.contains(selectedNode)) {
			if (text?.trim()) {
				const range = selection?.getRangeAt(0);
				const rect = range?.getBoundingClientRect();

				setButtonPosition({
					top: rect?.top - 40,
					left: rect?.left,
				});

				setShowButton(true);
				setSelectedText(text);
			} else {
				setShowButton(false);
			}
		}
	}, []);

	useEffect(() => {
		if (totalPages) {
			pageRefs.current = Array.from({ length: totalPages }, (_, i) => {
				return pageRefs.current[i] || React.createRef();
			});
		}
	}, [totalPages]);

	useEffect(() => {
		if (highlighting) {
			document.addEventListener("mouseup", handleMouseUp);
		} else {
			document.removeEventListener("mouseup", handleMouseUp);
		}
		return () => {
			document.removeEventListener("mouseup", handleMouseUp);
		};
	}, [highlighting, handleMouseUp]);

	const handleRemoveTextHighlight = () => {
		setIsHighlighting(false);
		setShowButton(false);
	};

	const storedTime = sessionStorage.getItem("password_entered_at");

	const handleAddSubmarkingSubmit = async (values: CaseEvidenceForm) => {
		const expirationTime = moment(storedTime).add(5, "minutes");
		if (storedTime && expirationTime !== undefined && moment().isBefore(expirationTime)) {
			await caseEvidenceSubmarkingMutation.mutateAsync(values);
		} else {
			try {
				modal.openModal({
					allowClose: true,
					title: "Confirm Submit",
					description: "Are you sure? This action cannot be undone.",
					size: "sm",
					showHeader: true,
					showFooter: false,
					component: (
						<ConfirmWithPasswordModal
							onSubmit={async () => {
								await caseEvidenceSubmarkingMutation.mutateAsync(values);
							}}
						/>
					),
				});
			} catch (error) {
				console.log(error);
			}
		}
	};

	const handleAddSubmarking = (selectedText: string) => {
		modal.openModal({
			title: "Add Submarking",
			description: `Add Submarking Evidence of Exhibit ${evidence?.exhibit}`,
			component: <CaseEvidenceEditModal selectedText={selectedText} data={undefined} newSubmarking={evidence} onSubmit={handleAddSubmarkingSubmit} />,
		});
	};

	return (
		<div className="flex flex-col shrink-0 w-full bg-background overflow-y-hidden">
			<div className="flex flex-row shrink-0 w-full h-[50px] px-4 border-b border-slate-200 justify-betwwen">
				<div className="inline-flex flex-row items-center w-full">
					<span className="text-base font-bold text-foreground-dark">Document Preview</span>
				</div>
				<div className="inline-flex flex-row items-center w-full">
					<div className="flex flex-row items-center justify-center w-full gap-1">
						{/* <Tooltip content="Go to first page" side="bottom">
              <Button onClick={() => setCurrentPage(1)} type="button" size="icon" variant="ghost">
                <IconChevronsLeft size={18} stroke={1.5} />
              </Button>
            </Tooltip>
            <Tooltip content="Go to first page" side="bottom">
              <Button onClick={() => setCurrentPage(currentPage - 1)} disabled={currentPage === 1} type="button" size="icon" variant="ghost">
                <IconChevronLeft size={18} stroke={1.5} />
              </Button>
            </Tooltip> */}
						<div className="flex flex-row items-center h-[28px] w-32">
							<Input
								className="text-center text-sm font-semibold h-full border border-border/50 outline-none text-foreground rounded-md"
								value={inputValue}
								onChange={handleInputChange}
								// type="number"
								min={1}
								max={totalPages}
							/>
							<span className="text-sm font-semibold inline-flex px-1 text-gray-300">/</span>
							<Input
								className="text-center text-sm font-semibold h-full border border-border/50 outline-none text-foreground rounded-md"
								value={totalPages}
								disabled
							/>
						</div>
						{/* <Tooltip content="Go to first page" side="bottom">
              <Button onClick={() => setCurrentPage(currentPage + 1)} disabled={!(currentPage < totalPages)} type="button" size="icon" variant="ghost">
                <IconChevronRight size={18} stroke={1.5} />
              </Button>
            </Tooltip>
            <Tooltip content="Go to first page" side="bottom">
              <Button onClick={() => setCurrentPage(totalPages)} disabled={!(currentPage < totalPages)} type="button" size="icon" variant="ghost">
                <IconChevronsRight size={18} stroke={1.5} />
              </Button>
            </Tooltip> */}
					</div>
				</div>
				<div className="inline-flex flex-row items-center w-full">
					<div className="flex flex-row justify-end w-full gap-1">
						{evidence && (
							<span>
								{!highlighting ? (
									<Tooltip content="Highlight Texts" side="bottom" className="z-[99]">
										<Button onClick={() => handleMouseUp()} type="button" size="icon" variant="ghost">
											<IconHighlight size={18} stroke={1.5} />
										</Button>
									</Tooltip>
								) : (
									<Tooltip content="Exit Highlight Mode" side="bottom" className="z-[99]">
										<Button onClick={() => handleRemoveTextHighlight()} type="button" size="icon" variant="danger">
											<IconHighlight size={18} stroke={1.5} />
										</Button>
									</Tooltip>
								)}
							</span>
						)}
						<Tooltip content="Present Full Screen" side="bottom" className="z-[99]">
							<Button onClick={() => setFullscreen(true)} type="button" size="icon" variant="ghost">
								<IconArrowsMaximize size={18} stroke={1.5} />
							</Button>
						</Tooltip>
						<Tooltip content="Rotate" side="bottom" className="z-[99]">
							<Button onClick={() => setRotateValue(rotateValue + 90)} type="button" size="icon" variant="ghost">
								<IconRotate size={18} stroke={1.5} />
							</Button>
						</Tooltip>
						<Tooltip content="Download" side="bottom" className="z-[99]">
							<Button
								onClick={() => {
									handleDownload(documents);
								}}
								type="button"
								size="icon"
								variant="ghost"
							>
								<IconFileDownload size={18} stroke={1.5} />
							</Button>
						</Tooltip>
						<Tooltip content="Print" side="bottom" className="z-[99]">
							<Button type="button" size="icon" variant="ghost">
								<IconPrinter size={18} stroke={1.5} />
							</Button>
						</Tooltip>
						{/* <Tooltip content="Settings" side="bottom" className="z-[99]">
							<Button type="button" size="icon" variant="ghost">
								<IconSettings size={18} stroke={1.5} />
							</Button>
						</Tooltip> */}
						{showButton && (
							<div
								style={{
									position: "absolute",
									top: `${buttonPosition.top}px`,
									left: `${buttonPosition.left}px`,
									zIndex: 1000,
								}}
							>
								<Button size="sm" type={"button"} onClick={() => handleAddSubmarking(selectedText)}>
									Add as Submarking
								</Button>
							</div>
						)}
					</div>
				</div>
			</div>

			<ScrollArea className="h-[calc(100svh-95px)]">
				{highlighting && (
					<div className="sticky top-0 start-0 z-50 flex items-center p-2 justify-center bg-primary-50 text-primary-600 text-sm font-semibold ">
						You're in highlight mode. Highlight texts to add as a submarking.
					</div>
				)}
				<div ref={contentRef} className="flex flex-col items-center w-full bg-slate-200 py-6">
					{/* <Document className="max-w-[800px]" rotate={rotateValue} file={file} onLoadSuccess={({ numPages }) => setTotalPages(numPages)}> */}
					<Document rotate={rotateValue} file={file} onLoadSuccess={({ numPages }) => setTotalPages(numPages)}>
						<div className="flex flex-col gap-4">
							{Array.from({ length: totalPages }, (_, i) => (
								<div key={i} ref={pageRefs.current[i]}>
									<Page pageNumber={i + 1} scale={1.3} width={maxWidth} renderTextLayer={true} />
								</div>
							))}
						</div>
					</Document>
				</div>
			</ScrollArea>

			{fullscreen && (
				<div className="fixed inset-0 z-[999] bg-foreground-dark overflow-auto">
					<div className="flex flex-row justify-end">
						<button className="p-4" type={"button"} onClick={() => setFullscreen(false)}>
							<IconX className="text-white" size={26} stroke={1.5} />
						</button>
					</div>

					<div className="flex flex-col items-center gap-4 py-4">
						<Document file={file} onLoadSuccess={({ numPages }) => setTotalPages(numPages)} loading={<p>Loading PDF...</p>} error={<p>Failed to load PDF.</p>}>
							<div className="flex flex-col gap-4">
								{Array.from({ length: totalPages }, (_, i) => (
									<Page key={i} pageNumber={i + 1} scale={1.5} />
								))}
							</div>
						</Document>
					</div>
				</div>
			)}
		</div>
	);
}
